import { useParams, Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { siteInfo } from "@/mocks/profile";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.id === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <title>分类未找到 - {siteInfo.name}</title>
        <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-foreground-950 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </div>
      </>
    );
  }

  const pageTitle = `${category.name} - ${siteInfo.name}`;
  const pageDesc = `${category.description}。${siteInfo.description}`;

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={`${category.name},${siteInfo.keywords}`} />
      <link rel="canonical" href={`${siteInfo.url}/category/${slug}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:url" content={`${siteInfo.url}/category/${slug}`} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />

      <div className="min-h-screen bg-background-50 pt-16">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: category.name },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="mb-10">
              <h1 className="text-3xl md:text-4xl font-semibold text-foreground-950 mb-3">
                <i className={`${category.icon} text-primary-500 mr-3`}></i>
                {category.name}
              </h1>
              <p className="text-foreground-600 text-sm md:text-base">{category.description}</p>
              <p className="text-xs text-foreground-500 mt-2">
                共 {categoryArticles.length} 篇内容
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${
                    cat.id === slug
                      ? "bg-primary-500 text-background-50 font-medium"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200/70"
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-100/80 transition-all duration-300"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-500">{article.date}</span>
                        <span className="text-xs text-foreground-500">·</span>
                        <span className="text-xs text-foreground-500 flex items-center gap-1">
                          <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                        </span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-2 py-0.5 bg-background-50 text-foreground-600 rounded-full whitespace-nowrap"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 text-foreground-500">
                <i className="ri-inbox-line text-4xl mb-4 block"></i>
                <p>该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}